import { logSwallowed } from '@/lib/errors';
// ============================================================================
// deployment.ts — РЕЖИМ РАЗВЁРТЫВАНИЯ БАЗЫ (закрытый контур), клиент (SSoT/SRP).
//
// Режим приходит в настройках базы: settings.deployment.mode
//   connected — обычный режим (есть доступ к интернету/внешним сервисам)
//   closed    — закрытый контур (внешние сервисы недоступны)
//   airgap    — полностью изолированный контур
//
// Источник — ТОТ ЖЕ, что у идентичности приложения (appIdentity → GetBaseSettings /
// ListBases): appIdentity применяет разобранные настройки через
// applyDeploymentSettings. СЕТЕВОГО ПУТИ здесь НЕТ — никаких дублей запросов.
//
// До гидратации настроек режим считается 'connected' (текущее поведение) —
// это НЕ скрытый фоллбэк, а явное начальное состояние «ещё не знаем».
// ============================================================================

import { useEffect } from 'react';
import { create } from 'zustand';

export type DeploymentMode = 'connected' | 'closed' | 'airgap';

/** Режим из настроек базы: deployment.mode; отсутствует/неизвестен — connected. */
export function resolveDeploymentMode(
  settings: Record<string, unknown> | null | undefined,
): DeploymentMode {
  if (!settings) return 'connected';
  const deployment = settings.deployment;
  if (!deployment || typeof deployment !== 'object') return 'connected';
  const mode = (deployment as Record<string, unknown>).mode;
  if (mode === 'closed' || mode === 'airgap') return mode;
  return 'connected';
}

/** Закрытый контур: нет доступа к интернету/внешним сервисам. */
export function isContour(mode: DeploymentMode): boolean {
  return mode === 'closed' || mode === 'airgap';
}

interface DeploymentState {
  mode: DeploymentMode;
  loaded: boolean;
}

/** Единый стор режима (читают компоненты и сервисы без React). */
export const useDeploymentStore = create<DeploymentState>(() => ({
  mode: 'connected',
  loaded: false,
}));

/** Применить настройки базы к режиму — ЕДИНАЯ точка (вызывает appIdentity). */
export function applyDeploymentSettings(
  settings: Record<string, unknown> | null | undefined,
): void {
  useDeploymentStore.setState({ mode: resolveDeploymentMode(settings), loaded: true });
}

/** Сброс при смене базы/перечитывании настроек (appIdentity.invalidateAppIdentity). */
export function resetDeployment(): void {
  useDeploymentStore.setState({ mode: 'connected', loaded: false });
}

/** Синхронный режим (для не-React сервисов); до гидратации — connected. */
export function getDeploymentMode(): DeploymentMode {
  return useDeploymentStore.getState().mode;
}

/** Гарантировать загрузку режима (сеет appIdentity — общий источник настроек). */
export function ensureDeploymentMode(): void {
  if (useDeploymentStore.getState().loaded) return;
  // Динамический импорт — разрываем статический цикл appIdentity ↔ deployment
  // (appIdentity статически импортирует нас для applyDeploymentSettings).
  void import('@/lib/appIdentity').then((m) => m.getAppSettings()).catch((e) => { logSwallowed(e, 'lib/deployment.ts:72'); return undefined; });
}

/** React-хук: режим развёртывания (единый источник для компонентов). */
export function useDeploymentMode(): DeploymentMode {
  const mode = useDeploymentStore((s) => s.mode);
  useEffect(() => {
    ensureDeploymentMode();
  }, []);
  return mode;
}

/** React-хук: база в закрытом контуре (адаптация UI — скрыть интернет-зависимое). */
export function useIsContour(): boolean {
  return isContour(useDeploymentMode());
}
